import { useState, useEffect } from 'react';
import { Slack, ExternalLink, Trash2, Check, AlertCircle, Save } from 'lucide-react';
import { api } from '../services/api';

interface SlackStatus {
  connected: boolean;
  team_name?: string;
  team_id?: string;
  channel?: string;
}

export default function IntegrationsTab() {
  const [loading, setLoading] = useState(true);
  const [slackStatus, setSlackStatus] = useState<SlackStatus>({ connected: false });
  const [disconnecting, setDisconnecting] = useState(false);
  const [savingChannel, setSavingChannel] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [channelForm, setChannelForm] = useState({ channelId: '', channelName: '' });

  useEffect(() => {
    loadSlackStatus();
    checkOAuthCallback();
  }, []);

  const loadSlackStatus = async () => {
    try {
      const status = await api.getSlackStatus();
      setSlackStatus(status);
    } catch (error) {
      console.error('Failed to load Slack status:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkOAuthCallback = () => {
    const params = new URLSearchParams(window.location.search);
    const slackParam = params.get('slack');

    if (slackParam === 'success') {
      setMessage({ type: 'success', text: 'Slack connected successfully!' });
      loadSlackStatus();
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (slackParam === 'error') {
      const errorMsg = params.get('message') || 'Failed to connect Slack';
      setMessage({ type: 'error', text: errorMsg });
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  const handleConnectSlack = async () => {
    try {
      const { auth_url } = await api.initSlackInstall();
      window.location.href = auth_url;
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to initiate Slack connection'
      });
    }
  };

  const handleDisconnectSlack = async () => {
    if (!confirm('Are you sure you want to disconnect Slack? You will stop receiving escalation alerts.')) {
      return;
    }

    setDisconnecting(true);
    try {
      await api.disconnectSlack();
      setSlackStatus({ connected: false });
      setMessage({ type: 'success', text: 'Slack disconnected successfully' });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to disconnect Slack'
      });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSaveChannel = async () => {
    if (!channelForm.channelId || !channelForm.channelName) {
      setMessage({ type: 'error', text: 'Please enter both channel ID and name' });
      return;
    }

    setSavingChannel(true);
    try {
      await api.updateSlackConfig({
        channel_id: channelForm.channelId,
        channel_name: channelForm.channelName
      });
      setMessage({ type: 'success', text: 'Channel configured successfully' });
      loadSlackStatus();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to save channel configuration'
      });
    } finally {
      setSavingChannel(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading integrations...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Messages */}
      {message && (
        <div className={`rounded-lg p-4 flex items-start gap-3 ${
          message.type === 'success'
            ? 'bg-green-50 border border-green-200'
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex-shrink-0 mt-0.5">
            {message.type === 'success' ? (
              <Check className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600" />
            )}
          </div>
          <div className="flex-1">
            <p className={`text-sm font-medium ${
              message.type === 'success' ? 'text-green-800' : 'text-red-800'
            }`}>
              {message.text}
            </p>
          </div>
          <button
            onClick={() => setMessage(null)}
            className={`${
              message.type === 'success' ? 'text-green-600 hover:text-green-800' : 'text-red-600 hover:text-red-800'
            }`}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}

      {/* Slack Integration Card */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                <Slack className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">Slack</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Get real-time escalation alerts in Slack when customers need human help
                </p>

                {slackStatus.connected ? (
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm font-medium text-gray-900">Connected</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <div><span className="font-medium">Workspace:</span> {slackStatus.team_name}</div>
                      {slackStatus.channel && (
                        <div><span className="font-medium">Channel:</span> {slackStatus.channel}</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="mt-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="text-sm font-medium text-gray-500">Not connected</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Channel Configuration */}
          {slackStatus.connected && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Channel Configuration</h4>
              <p className="text-xs text-gray-500 mb-4">
                Configure which Slack channel receives escalation alerts. Invite the bot to your channel with <code className="px-1 py-0.5 bg-gray-200 rounded text-xs">/invite @Tahc</code>
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Channel ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={channelForm.channelId}
                    onChange={(e) => setChannelForm({ ...channelForm, channelId: e.target.value })}
                    placeholder="C01234567"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Right-click channel → View channel details
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Channel Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={channelForm.channelName}
                    onChange={(e) => setChannelForm({ ...channelForm, channelName: e.target.value })}
                    placeholder="#support"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                  />
                </div>
              </div>
              <button
                onClick={handleSaveChannel}
                disabled={savingChannel}
                className="mt-3 flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                {savingChannel ? 'Saving...' : 'Save Channel'}
              </button>
            </div>
          )}

          <div className="mt-6 flex items-center gap-3">
            {slackStatus.connected ? (
              <>
                <button
                  onClick={handleDisconnectSlack}
                  disabled={disconnecting}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4" />
                  {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                </button>
                <a
                  href="https://api.slack.com/apps"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                >
                  Manage in Slack
                  <ExternalLink className="w-4 h-4" />
                </a>
              </>
            ) : (
              <button
                onClick={handleConnectSlack}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 transition"
              >
                <Slack className="w-4 h-4" />
                Connect Slack
              </button>
            )}
          </div>
        </div>

        {/* Features List */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">Features</h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>Instant alerts when conversations are escalated to humans</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>Interactive buttons to take over conversations or view in dashboard</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>Reply to customers directly from Slack threads</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>See customer context and message history in alerts</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Coming Soon Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Coming Soon</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M13.4 2.096a10.08 10.08 0 00-8.937 3.655A10.054 10.054 0 002.096 13.4c.53 3.894 3.458 7.207 7.285 8.246a9.982 9.982 0 002.618.354l.21-.005-.223-2-.223 2A8.014 8.014 0 0120 14a8.014 8.014 0 00-8-8V4a10.013 10.013 0 0110 10 10.013 10.013 0 01-10 10h-.351l-1.898-.232-.166-.028C5.854 22.645 2.5 18.842 2.5 14.5a10.517 10.517 0 012.3-6.498l-1.4-1.4A12.517 12.517 0 00.5 14.5c0 5.247 4.253 9.5 9.5 9.5a12.013 12.013 0 0012-12 12.013 12.013 0 00-12-12l.4 2.096z"/>
              </svg>
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Zapier</h4>
              <p className="text-xs text-gray-500 mt-1">Connect with 5,000+ apps</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M22.46 6c-.85.38-1.78.64-2.75.76a4.8 4.8 0 002.1-2.66c-.92.55-1.95.95-3.03 1.16A4.76 4.76 0 0015.45 4a4.77 4.77 0 00-4.76 4.76c0 .37.04.74.12 1.1A13.5 13.5 0 013.39 4.84a4.76 4.76 0 001.47 6.36 4.73 4.73 0 01-2.16-.6v.06a4.76 4.76 0 003.82 4.67c-.4.11-.82.17-1.25.17-.31 0-.61-.03-.9-.08a4.76 4.76 0 004.45 3.31A9.56 9.56 0 011.64 20c-.39 0-.77-.02-1.15-.07a13.5 13.5 0 007.29 2.14c8.75 0 13.54-7.25 13.54-13.54 0-.21 0-.41-.01-.62A9.63 9.63 0 0024 5.32c-.87.39-1.8.65-2.79.77z"/>
              </svg>
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Webhooks</h4>
              <p className="text-xs text-gray-500 mt-1">Custom HTTP callbacks</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
